import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faStop, faHeadphones, faSpinner } from "@fortawesome/free-solid-svg-icons";
import useLessonAudio from "../../hooks/useLessonAudio";
import { markdownToSpeakableText } from "../../utils/markdownToSpeech";
import "./ListenButton.css";

// Map the i18next UI language to a BCP-47 voice locale. Spanish-first for the
// US-Latino audience (es-MX); the hook's voice picker falls back to any es-*
// voice, then to the engine default if none is installed.
const localeForLanguage = (language) => {
    const base = (language || "en").split("-")[0].toLowerCase();
    return base === "es" ? "es-MX" : "en-US";
};

// The content locale the backend keys narration on ("en" / "es") — distinct from
// the BCP-47 voice locale above.
const contentLocaleForLanguage = (language) =>
    (language || "en").split("-")[0].toLowerCase() === "es" ? "es" : "en";

// Playback speeds, cycled in order by the speed button. 2× is the ceiling: past
// that the browser fallback voice stops being intelligible.
const SPEEDS = [1, 1.25, 1.5, 2];
const RATE_STORAGE_KEY = "codenec.lessonAudio.rate";

// "1×", "1.5×" — same notation in both languages, so it needs no translation.
const formatRate = (rate) => `${rate}×`;

// The chosen speed outlives the lesson: a student who wants 1.5× wants it for
// the whole course, not one page. Unreadable storage (private mode, blocked
// cookies) just means the default.
const readStoredRate = () => {
    try {
        const stored = parseFloat(window.localStorage.getItem(RATE_STORAGE_KEY));
        return SPEEDS.includes(stored) ? stored : 1;
    } catch (err) {
        return 1;
    }
};

const storeRate = (rate) => {
    try {
        window.localStorage.setItem(RATE_STORAGE_KEY, String(rate));
    } catch (err) {
        /* not worth surfacing — the speed still applies for this session */
    }
};

/**
 * ListenButton
 * ------------
 * "Listen to the lesson" control. Strips the lesson markdown to plain prose and
 * plays it aloud via {@link useLessonAudio}, which uses the premium ElevenLabs
 * voice when `REACT_APP_TTS_PROVIDER` is set and a clip exists, and the browser's
 * built-in voice otherwise. Purely presentational — all audio mechanics live in
 * the hook.
 *
 * The pill next to it cycles playback speed through {@link SPEEDS}; the choice is
 * remembered across lessons and applies to whichever engine is playing.
 *
 * @param {{ text?: string, lessonID?: string, className?: string }} props -
 *   `text` is the raw lesson markdown (e.g. `definition.lessonMarkdown`);
 *   `lessonID` is what the backend keys the pre-generated narration on. Without
 *   it the button still works, just on the browser voice.
 */
const ListenButton = ({ text, lessonID, className = "" }) => {
    const { t, i18n } = useTranslation();
    const lang = localeForLanguage(i18n.language);
    const locale = contentLocaleForLanguage(i18n.language);
    const speakable = useMemo(() => markdownToSpeakableText(text), [text]);
    const [rate, setRate] = useState(readStoredRate);
    const { supported, status, play, pause, stop } = useLessonAudio({
        text: speakable,
        lang,
        lessonID,
        locale,
        rate,
    });

    const cycleRate = useCallback(() => {
        setRate((current) => {
            const next = SPEEDS[(SPEEDS.indexOf(current) + 1) % SPEEDS.length];
            storeRate(next);
            return next;
        });
    }, []);

    // Nothing to offer if there's no speech engine at all, or no prose to read.
    if (!supported || !speakable.trim()) return null;

    const isPlaying = status === "playing";
    const isPaused = status === "paused";
    const isLoading = status === "loading";
    const isActive = isPlaying || isPaused;

    const mainLabel = isLoading
        ? t("lessonAudio.loading")
        : isPlaying
            ? t("lessonAudio.pause")
            : isPaused
                ? t("lessonAudio.resume")
                : t("lessonAudio.listen");

    // idle → headphones, loading → spinner, playing → pause, paused → play.
    const mainIcon = isLoading
        ? faSpinner
        : isActive
            ? (isPlaying ? faPause : faPlay)
            : faHeadphones;

    return (
        <div className={`listen-button ${className}`.trim()}>
            <Button
                type="button"
                size="sm"
                className="listen-button__main"
                onClick={isPlaying ? pause : play}
                disabled={isLoading}
                aria-pressed={isActive}
                aria-label={mainLabel}
                title={mainLabel}
            >
                <FontAwesomeIcon icon={mainIcon} spin={isLoading} />
                <span className="listen-button__label">{mainLabel}</span>
            </Button>
            <Button
                type="button"
                size="sm"
                className="listen-button__speed"
                onClick={cycleRate}
                aria-label={`${t("lessonAudio.speed")}: ${formatRate(rate)}`}
                title={t("lessonAudio.speedHint")}
            >
                {formatRate(rate)}
            </Button>
            {isActive && (
                <Button
                    type="button"
                    size="sm"
                    className="listen-button__stop"
                    onClick={stop}
                    aria-label={t("lessonAudio.stop")}
                    title={t("lessonAudio.stop")}
                >
                    <FontAwesomeIcon icon={faStop} />
                </Button>
            )}
        </div>
    );
};

export default ListenButton;
